import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { loginRequest } from "./auth/authConfig";
import { Header } from "./components/Header";
import { Sidebar } from "./components/Sidebar";
import { MobileDrawer } from "./components/MobileDrawer";
import { Footer } from "./components/Footer";
import { ChatLauncher } from "./components/ChatLauncher";
import { ListPage } from "./pages/ListPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DashboardNewPage } from "./pages/DashboardNewPage";
import { ChampionsNewPage } from "./pages/ChampionsNewPage";
import { InsightsPage } from "./pages/InsightsPage";
import { TrainingPage } from "./pages/TrainingPage";
import { AdminPage } from "./pages/AdminPage";
import { ActivityPage } from "./pages/ActivityPage";
import { useTheme } from "./hooks/useTheme";
import { useSidebarState } from "./hooks/useSidebarState";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { useIsAdmin } from "./hooks/useIsAdmin";
import { useIsChampion } from "./hooks/useIsChampion";
import { logActivity } from "./api/activityLog";

function AuthGate({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      void instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, inProgress, instance]);

  if (!isAuthenticated) return null;
  return <>{children}</>;
}

/**
 * Gate for admin-only pages. Non-admins are bounced back to the dashboard
 * silently — the Admin link is also hidden from their profile menu, so they
 * should never hit this route through normal navigation.
 */
function AdminGuard({ children }: { children: ReactNode }) {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Gate for pages in preview with the AI Champions: champions and admins only. */
function ChampionGuard({ children }: { children: ReactNode }) {
  const isChampion = useIsChampion();
  if (!isChampion) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const PAGE_LABELS: Record<string, string> = {
  "/": "Dashboard",
  "/dashboard-new": "Dashboard (New)",
  "/insights": "Insights",
  "/library": "AI Library",
  "/initiatives": "AI Initiatives",
  "/platforms": "AI Platforms",
  "/training": "AI Training",
  "/admin": "Admin",
  "/champions-new": "AI Champions (New)",
  "/activity": "Activity Log",
};

// Module-level, not a ref — StrictMode double-invokes mount effects with a
// fresh component instance each time, so a ref would reset too and we'd
// log every page visit twice in dev.
let lastLoggedPath: string | null = null;

/** Logs a "PageView" entry on every route change. Lives in its own
 *  component (rather than inline in AppShell) purely so useLocation's
 *  re-render on navigation doesn't force the whole shell to re-render. */
function PageViewLogger() {
  const location = useLocation();
  useEffect(() => {
    if (lastLoggedPath === location.pathname) return;
    lastLoggedPath = location.pathname;
    const label = PAGE_LABELS[location.pathname] ?? location.pathname;
    void logActivity("Navigation", "PageView", `Visited ${label}`, "page", location.pathname);
  }, [location.pathname]);
  return null;
}

function AppShell() {
  const { instance } = useMsal();
  const { theme, toggleTheme } = useTheme();
  const { collapsed, toggle: toggleSidebar } = useSidebarState();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Logged BEFORE the redirect, not after — logoutRedirect navigates away
  // immediately, so there's no reliable "post-logout" moment to hook into.
  // Capped at 1.5s so a slow/failed log call can't meaningfully delay sign-out.
  const signOut = async () => {
    const name = instance.getActiveAccount()?.name || "Someone";
    await Promise.race([
      logActivity("Auth", "Logout", `${name} signed out`),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]);
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const sidebarProps = {
    collapsed: isMobile ? false : collapsed,
    onToggleCollapsed: toggleSidebar,
    theme,
    onToggleTheme: toggleTheme,
  };

  return (
    <div
      className={`app-layout${
        collapsed && !isMobile ? " app-layout-collapsed" : ""
      }`}
    >
      {!isMobile ? (
        <Sidebar {...sidebarProps} showCollapseToggle />
      ) : (
        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <Sidebar
            {...sidebarProps}
            showCollapseToggle={false}
            onNavigate={() => setDrawerOpen(false)}
          />
        </MobileDrawer>
      )}

      <div className="app-content">
        <PageViewLogger />
        <Header
          onMobileMenuToggle={() => setDrawerOpen(true)}
          onSignOut={signOut}
        />
        <main className="app-main">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route
              path="/dashboard-new"
              element={
                <ChampionGuard>
                  <DashboardNewPage />
                </ChampionGuard>
              }
            />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/library" element={<ListPage kind="library" />} />
            <Route
              path="/initiatives"
              element={<ListPage kind="initiative" />}
            />
            <Route path="/platforms" element={<ListPage kind="platform" />} />
            <Route path="/training" element={<TrainingPage />} />
            <Route
              path="/admin"
              element={
                <AdminGuard>
                  <AdminPage />
                </AdminGuard>
              }
            />
            <Route
              path="/champions-new"
              element={
                <ChampionGuard>
                  <ChampionsNewPage />
                </ChampionGuard>
              }
            />
            <Route
              path="/activity"
              element={
                <AdminGuard>
                  <ActivityPage />
                </AdminGuard>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
      <ChatLauncher theme={theme} />
    </div>
  );
}

export default function App() {
  return (
    <AuthGate>
      <AppShell />
    </AuthGate>
  );
}
